import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { PlusCircle, Edit, Trash2, Eye, EyeOff, Lock } from 'lucide-react';

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(true);
  const [loginPassword, setLoginPassword] = useState('');
  const [authToken, setAuthToken] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image_url: '',
    published: true
  });

  useEffect(() => {
    // Check if already authenticated
    const token = localStorage.getItem('blog_admin_token');
    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);
      setShowLoginModal(false);
      fetchPosts();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: loginPassword })
      });

      const data = await response.json();

      if (data.success && data.token) {
        setAuthToken(data.token);
        setIsAuthenticated(true);
        setShowLoginModal(false);
        localStorage.setItem('blog_admin_token', data.token);
        toast.success('Login successful!');
        fetchPosts();
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed');
    } finally {
      setLoading(false);
      setLoginPassword('');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('blog_admin_token');
    setAuthToken(null);
    setIsAuthenticated(false);
    setShowLoginModal(true);
    toast.success('Logged out');
  };

  const fetchPosts = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog/posts?published_only=false`);
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load blog posts');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const url = editingPost 
        ? `${BACKEND_URL}/api/blog/posts/${editingPost.id}`
        : `${BACKEND_URL}/api/blog/posts`;
      
      const method = editingPost ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingPost ? 'Post updated!' : 'Post created!');
        setFormData({ title: '', content: '', excerpt: '', image_url: '', published: true });
        setShowForm(false);
        setEditingPost(null);
        fetchPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save post');
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Failed to save post');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      image_url: post.image_url || '',
      published: post.published
    });
    setShowForm(true);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/blog/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        toast.success('Post deleted!');
        fetchPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-teal-600" />
              Admin Login
            </DialogTitle>
            <DialogDescription>
              Enter password to access the blog admin panel
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Enter admin password"
                required
                className="mt-2"
                autoFocus
              />
            </div>
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {isAuthenticated && (
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-4xl font-bold text-slate-900">Blog Admin</h1>
                <p className="text-slate-600 mt-2">Manage your Casa Bahiamar blog posts</p>
              </div>
              <div className="flex gap-3">
                <Button 
                  onClick={() => {
                    setShowForm(!showForm);
                    setEditingPost(null);
                    setFormData({ title: '', content: '', excerpt: '', image_url: '', published: true });
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <PlusCircle className="w-4 h-4 mr-2" />
                  New Post
                </Button>
                <Button 
                  onClick={handleLogout}
                  variant="outline"
                  className="border-slate-300"
                >
                  Logout
                </Button>
              </div>
            </div>

          {showForm && (
            <Card className="mb-8 border-teal-200">
              <CardHeader>
                <CardTitle>{editingPost ? 'Edit Post' : 'Create New Post'}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="e.g., Best Fishing Spots in Key Largo"
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="excerpt">Excerpt (Optional)</Label>
                    <Textarea
                      id="excerpt"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleInputChange}
                      placeholder="Brief summary of the post..."
                      rows={2}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="content">Content *</Label>
                    <Textarea
                      id="content"
                      name="content"
                      value={formData.content}
                      onChange={handleInputChange}
                      placeholder="Write your blog post content here..."
                      rows={12}
                      required
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="image_url">Image URL (Optional)</Label>
                    <Input
                      id="image_url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com/image.jpg"
                      className="mt-2"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="published"
                      name="published"
                      checked={formData.published}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-teal-600 rounded"
                    />
                    <Label htmlFor="published" className="cursor-pointer">
                      Publish immediately
                    </Label>
                  </div>

                  <div className="flex gap-4">
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {loading ? 'Saving...' : editingPost ? 'Update Post' : 'Create Post'}
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setEditingPost(null);
                        setFormData({ title: '', content: '', excerpt: '', image_url: '', published: true });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-slate-900">All Posts ({posts.length})</h2>
            
            {posts.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="pt-6 text-center py-12">
                  <p className="text-slate-600">No blog posts yet. Create your first post!</p>
                </CardContent>
              </Card>
            ) : (
              posts.map((post) => (
                <Card key={post.id} className="border-slate-200">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-slate-900">{post.title}</h3>
                          {post.published ? (
                            <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                              <Eye className="w-3 h-3" />
                              Published
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">
                              <EyeOff className="w-3 h-3" />
                              Draft
                            </span>
                          )}
                        </div>
                        <p className="text-slate-600 mb-3 line-clamp-2">{post.excerpt}</p>
                        <p className="text-sm text-slate-500">
                          Created: {formatDate(post.created_at)} • 
                          Updated: {formatDate(post.updated_at)}
                        </p>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(post)}
                          className="border-teal-600 text-teal-600 hover:bg-teal-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminBlog;
